import { logger } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GET_PATIENT_PROFILES,
  UPDATE_PATIENT_PROFILE,
  type UpdatePatientProfileRequest,
} from '../../contracts';
import { updatePatientProfile } from '../api/patients';

export type UpdateOwnPatientProfileArgs = {
  patientProfileId: string;
  patientProfile: UpdatePatientProfileRequest;
  /** Passed as a query param for session direct-share permission check.
   *  When a session is directly shared, the sharee can process (create/update/link/unlink)
   *  patient profiles on that session by providing its ID here. */
  sessionId?: string;
};

/**
 * The FIRST-PERSON half of `useMutateUpdatePatientProfile` (D2.2, ARCFE-1053).
 *
 * `PATCH /patient-profiles/{id}` is patients'; `PATCH /teams/{teamId}/members/
 * {memberId}/patient-profiles/{id}` is workspace's and stayed in the app hub
 * with the review-mode branch that picks between them.
 *
 * EVERYTHING SESSION-SHAPED STAYED APP-SIDE, per child design D2: the
 * `useQueryLinkedSessionsByProfileId` read, the sessions-list invalidation, and
 * the optimistic `GET_SESSION_BY_ID` / `GET_TEAM_MEMBER_SESSION_BY_ID` patches
 * are Session's cache, and app integration sequences them. That is also why
 * this hook no longer needs `patientProfileId` as a parameter — the only thing
 * it was for was the linked-sessions read.
 *
 * `useQueryClient()` replaces the legacy module-scope `reactQueryClient`
 * import. Same object at runtime: `PersistQueryClientProvider` is mounted with
 * exactly that singleton (`src/components/utils/minimal-providers.tsx`).
 *
 * SIDE-EFFECT ORDER: legacy ran the Session reactions BEFORE this invalidation
 * and the toast after it; both now run inside `onUpdated`, after, in their
 * original order relative to each other. The Session reactions touch only
 * Session's keys, which are disjoint from `GET_PATIENT_PROFILES`, so no
 * ordering is observable.
 */
export function useMutateUpdateOwnPatientProfile({
  isViewingOtherSession = false,
  onUpdated,
  onUpdateFailed,
}: {
  /** `isViewingOtherSessionAtom`, app-resolved (cohort-C). */
  isViewingOtherSession?: boolean;
  onUpdated?: (input: UpdateOwnPatientProfileArgs) => void;
  onUpdateFailed?: (error: unknown) => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_PATIENT_PROFILE],
    mutationFn: async (input: UpdateOwnPatientProfileArgs) => {
      return await updatePatientProfile(
        input.patientProfileId,
        input.patientProfile,
        isViewingOtherSession ? input.sessionId : undefined
      );
    },
    onSuccess: (_, input) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_PATIENT_PROFILES],
      });
      onUpdated?.(input);
    },
    onError: (error) => {
      logger.error('Error:', error);
      // Surfaces are responsible for catching the rejection from
      // `mutateAsync` and rendering inline error UI when they pass
      // `showToast: false` (e.g. the patient drawer / page inline edits).
      // The global toast is only useful when there is no per-field error
      // surface to land on — so the `showToast` narrowing is the delegate's.
      onUpdateFailed?.(error);
    },
  });
}
